// src/components/MobileAdcoTable.jsx
import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const MobileAdcoTable = ({ data, condensedView = false }) => {
  const theme = useTheme();

  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No ADCO data available
        </Typography>
      </Box>
    );
  }

  // Format cell value
  const formatValue = (value, columnName) => {
    if (value === null || value === undefined || value === '') return '-';

    // Handle date columns - format as YYYY-MM-DD in the UTC frame so a date-only
    // value isn't shifted back a day for clients west of UTC.
    const isDateColumn = columnName === 'DATE' || columnName === 'ASOFDT' || columnName.toLowerCase().includes('date');
    if (isDateColumn) {
      const parsed = dayjs.utc(value);
      return parsed.isValid() ? parsed.format('YYYY-MM-DD') : value;
    }

    // Format numeric metrics with 2 decimal places
    if (typeof value === 'number') {
      return value.toFixed(2);
    }

    return String(value);
  };

  // Transform data for condensed view
  if (condensedView) {
    const metrics = ['CRR', 'CDR', 'SEV', 'DLNQ60'];
    
    const condensedData = metrics.map(metric => {
      // Collect all values for this metric from data
      const values = data
        .map(row => {
          const val = row[metric];
          if (val == null) return '';
          return formatValue(val, metric);
        })
        .filter(v => v !== '');
      
      return {
        metric: metric,
        values: values.join('  ')  // Double space for better readability
      };
    }).filter(item => item.values); // Only include metrics with values

    return (
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '60vh',
          overflowX: 'auto',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#f8fafc',
                  color: theme.palette.mode === 'dark' ? 'white' : '#1e293b',
                  fontWeight: 600,
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  py: 2,
                  px: 2,
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  width: '100px'
                }}
              >
                Metric
              </TableCell>
              <TableCell
                sx={{
                  bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#f8fafc',
                  color: theme.palette.mode === 'dark' ? 'white' : '#1e293b',
                  fontWeight: 600,
                  fontSize: '14px',
                  py: 2,
                  px: 2,
                  borderBottom: `2px solid ${theme.palette.divider}`
                }}
              >
                Values
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {condensedData.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={{
                  '&:nth-of-type(odd)': {
                    bgcolor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.paper, 0.5)
                      : alpha(theme.palette.primary.main, 0.02)
                  },
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.primary.main, 0.05)
                  }
                }}
              >
                <TableCell
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    py: 2,
                    px: 2,
                    whiteSpace: 'nowrap',
                    color: theme.palette.text.primary
                  }}
                >
                  {row.metric}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: '14px',
                    py: 2,
                    px: 2,
                    color: theme.palette.text.primary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {row.values}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Standard view - show all columns
  const columns = Object.keys(data[0]);

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: '60vh',
        overflowX: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column}
                sx={{
                  bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#f8fafc',
                  color: theme.palette.mode === 'dark' ? 'white' : '#1e293b',
                  fontWeight: 600,
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  py: 2,
                  px: 2,
                  borderBottom: `2px solid ${theme.palette.divider}`
                }}
              >
                {column.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              sx={{
                '&:nth-of-type(odd)': {
                  bgcolor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.5)
                    : alpha(theme.palette.primary.main, 0.02)
                },
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.1)
                    : alpha(theme.palette.primary.main, 0.05)
                }
              }}
            >
              {columns.map((column) => (
                <TableCell
                  key={column}
                  sx={{
                    fontSize: '14px',
                    py: 2,
                    px: 2,
                    whiteSpace: 'nowrap',
                    color: theme.palette.text.primary
                  }}
                >
                  {formatValue(row[column], column)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MobileAdcoTable;

